import { Button } from '@cloudflare/kumo';
import { useQuery } from '@tanstack/react-query';
import { createRoute } from '@tanstack/react-router';
import type { ContentObject, SiteSnapshot } from '../../shared/content.ts';
import { siteQuery } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { SiteAssistantConfigurationEditor } from '../components/site-assistant-settings.tsx';
import { useContentDraft } from '../use-content-draft.ts';
import { Route as parent } from './site.tsx';

export const Route = createRoute({
  getParentRoute: () => parent,
  path: 'site-assistant',
  component: RouteComponent,
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const site = useQuery(siteQuery(siteId, { includePages: false }));
  if (site.isPending) return <Loading />;
  if (site.error) return <ErrorNotice error={site.error} />;
  const snapshot = site.data!;
  const settings = snapshot.objects.find((object) => object.kind === 'Settings');
  return settings?.kind === 'Settings' ? <SiteAssistantForm object={settings} snapshot={snapshot} /> : null;
}

function SiteAssistantForm({
  object,
  snapshot,
}: {
  object: Extract<ContentObject, { kind: 'Settings' }>;
  snapshot: SiteSnapshot;
}) {
  const { draft, setDraft, changed, save } = useContentDraft(snapshot.siteId, object);
  return (
    <section className="max-w-212.5 pt-7 pb-12.5 min-[761px]:pt-[35px] min-[761px]:pb-17.5 grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-5">
        <h2 className="text-[20px] font-semibold tracking-[-0.35px]">Site assistant</h2>
        <Button variant="primary" onClick={() => save.mutate()} disabled={!changed.length || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
      <ErrorNotice error={save.error} />
      <SiteAssistantConfigurationEditor
        value={draft}
        forms={snapshot.objects.filter((object) => object.kind === 'Form')}
        onChange={setDraft}
      />
    </section>
  );
}
